import React, { useEffect, useState } from 'react';

// Mobile-only homepage hint that points at the hamburger menu in the
// top-right of the header. A paintbrush-style loop draws around the
// icon, then a curved arrow swipes up to it. The whole overlay fades
// and replays for the first 30 seconds so new visitors can find the
// navigation; after that the timer unmounts the overlay entirely.
//
// Layout assumes Header.tsx's mobile button: container-content (px-6),
// button `p-2 -mr-2`, icon `w-6 h-6`. That puts the icon centre at
// about (right: 36px, top: 32px), so in our 200×200 top-right canvas
// the icon centre is at SVG (164, 32).
//
// Paintbrush look uses an SVG turbulence + displacement filter — the
// noise distorts the rendered stroke edge so straight lines pick up
// organic wobble, like a brushstroke. (CSS-Tricks "Squiggly Text" /
// "Wobbly Borders with SVG Filters" trick, applied to strokes.)

const HamburgerHintOverlay: React.FC = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = () => window.innerWidth < 768;
    if (!isMobile()) return;

    setShow(true);
    const handleResize = () => {
      if (!isMobile()) setShow(false);
    };
    window.addEventListener('resize', handleResize);
    const timer = window.setTimeout(() => setShow(false), 30_000);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.clearTimeout(timer);
    };
  }, []);

  if (!show) return null;

  return (
    <div aria-hidden="true" className="hamburger-hint-overlay md:hidden">
      <svg
        viewBox="0 0 200 200"
        width="200"
        height="200"
        fill="none"
        stroke="#b8731f"
        strokeWidth="4.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <defs>
          {/* Brush texture: fractal noise + displacement map distorts
              the stroke pixel-by-pixel so the painted edge looks
              organic instead of geometric. baseFrequency controls how
              fine the wobble is; scale controls how far each pixel can
              shift. Values picked to read as a paintbrush rather than
              a sketchy pencil line. */}
          <filter
            id="hintBrush"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
            filterUnits="objectBoundingBox"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.85"
              numOctaves="2"
              seed="5"
              result="noise"
            />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="2.4" />
          </filter>
        </defs>

        <g filter="url(#hintBrush)">
          {/* Loop around the hamburger (centre 164, 32; ellipse rx=27
              ry=24). Sweeps clockwise from the top through ~315°, then
              stops at upper-left — leaving an intentional gap so the
              stroke reads as a single brush mark, not a closed ring. */}
          <path className="hint-loop" d="M 164 5 A 27 24 0 1 1 145 15" />

          {/* Arrow shaft: short curve climbing from lower-left up to
              just under the icon. Kept short so the arrow feels like a
              pointer, not a long flourish. */}
          <path className="hint-shaft" d="M 108 116 C 117 100, 127 84, 138 62" />

          {/* Arrowhead: two lines from the tip (138, 62), symmetric
              around the shaft's tangent direction at that point so the
              head reads as a proper arrow rather than two stray ticks. */}
          <path className="hint-head" d="M 138 62 L 128 69 M 138 62 L 137 74" />
        </g>
      </svg>

      <style>{`
        .hamburger-hint-overlay {
          position: fixed;
          top: 0;
          right: 0;
          width: 200px;
          height: 200px;
          z-index: 50;
          pointer-events: none;
        }
        .hamburger-hint-overlay path {
          stroke-opacity: 0.92;
        }
        /* Each path animates its own dashoffset on the same 5.5s clock
           so the loop draws first, the shaft follows, then the head
           "completes" the arrow — all in lock-step every cycle. */
        .hamburger-hint-overlay .hint-loop {
          stroke-dasharray: 260;
          stroke-dashoffset: 260;
          opacity: 0;
          animation: hintLoopDraw 5.5s ease-in-out infinite;
        }
        .hamburger-hint-overlay .hint-shaft {
          stroke-dasharray: 140;
          stroke-dashoffset: 140;
          opacity: 0;
          animation: hintShaftDraw 5.5s ease-in-out infinite;
        }
        .hamburger-hint-overlay .hint-head {
          stroke-dasharray: 60;
          stroke-dashoffset: 60;
          opacity: 0;
          animation: hintHeadDraw 5.5s ease-in-out infinite;
        }
        @keyframes hintLoopDraw {
          0%   { stroke-dashoffset: 260; opacity: 0;    }
          3%   { stroke-dashoffset: 260; opacity: 0.95; }
          32%  { stroke-dashoffset: 0;   opacity: 0.95; }
          75%  { stroke-dashoffset: 0;   opacity: 0.95; }
          88%  { stroke-dashoffset: 0;   opacity: 0;    }
          100% { stroke-dashoffset: 0;   opacity: 0;    }
        }
        @keyframes hintShaftDraw {
          0%, 28% { stroke-dashoffset: 140; opacity: 0;    }
          31%     { stroke-dashoffset: 140; opacity: 0.95; }
          50%     { stroke-dashoffset: 0;   opacity: 0.95; }
          75%     { stroke-dashoffset: 0;   opacity: 0.95; }
          88%     { stroke-dashoffset: 0;   opacity: 0;    }
          100%    { stroke-dashoffset: 0;   opacity: 0;    }
        }
        @keyframes hintHeadDraw {
          0%, 48% { stroke-dashoffset: 60; opacity: 0;    }
          51%     { stroke-dashoffset: 60; opacity: 0.95; }
          60%     { stroke-dashoffset: 0;  opacity: 0.95; }
          75%     { stroke-dashoffset: 0;  opacity: 0.95; }
          88%     { stroke-dashoffset: 0;  opacity: 0;    }
          100%    { stroke-dashoffset: 0;  opacity: 0;    }
        }
        @media (prefers-reduced-motion: reduce) {
          .hamburger-hint-overlay .hint-loop,
          .hamburger-hint-overlay .hint-shaft,
          .hamburger-hint-overlay .hint-head {
            animation: none;
            stroke-dashoffset: 0;
            opacity: 0.92;
          }
        }
      `}</style>
    </div>
  );
};

export default HamburgerHintOverlay;
